import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, DollarSign, Clock, Zap, AlertTriangle } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

interface ModelPerformance {
  model_used: string;
  feature: string;
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
  fallback_calls: number;
  avg_response_time_ms: number;
  avg_tokens: number;
  total_cost_usd: number;
  success_rate_pct: number;
  last_used_at: string;
}

interface PromptPerformance {
  prompt_name: string;
  prompt_id: string;
  model_used: string;
  total_executions: number;
  avg_response_time_ms: number;
  avg_tokens: number;
  total_cost_usd: number;
  fallback_count: number;
  success_rate_pct: number;
}

const AdminLLMPerformance: React.FC = () => {
  const navigate = useNavigate();
  const [modelPerformance, setModelPerformance] = useState<ModelPerformance[]>([]);
  const [promptPerformance, setPromptPerformance] = useState<PromptPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadPerformanceData();
  }, [selectedTimeRange]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);

      const [modelPerfResult, promptPerfResult] = await Promise.all([
        supabase
          .from('llm_model_performance')
          .select('*')
          .order('total_calls', { ascending: false }),
        supabase
          .from('llm_prompt_performance')
          .select('*')
          .order('total_executions', { ascending: false })
      ]);

      if (modelPerfResult.error) throw modelPerfResult.error;
      if (promptPerfResult.error) throw promptPerfResult.error;

      setModelPerformance(modelPerfResult.data || []);
      setPromptPerformance(promptPerfResult.data || []);
    } catch (error: any) {
      console.error('Error loading performance data:', error);
      toast.error('Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    return {
      totalCalls: modelPerformance.reduce((sum, m) => sum + m.total_calls, 0),
      totalCost: modelPerformance.reduce((sum, m) => sum + Number(m.total_cost_usd), 0),
      avgSuccessRate: modelPerformance.length > 0
        ? modelPerformance.reduce((sum, m) => sum + Number(m.success_rate_pct), 0) / modelPerformance.length
        : 0,
      totalFallbacks: modelPerformance.reduce((sum, m) => sum + m.fallback_calls, 0)
    };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/llm')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to LLM Configuration
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">LLM Performance Analytics</h1>
            <p className="text-gray-600">Monitor model performance, costs, and optimization opportunities</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total API Calls</h3>
            <Activity className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totals.totalCalls.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Cost</h3>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${totals.totalCost.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">OpenRouter spend</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Success Rate</h3>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totals.avgSuccessRate.toFixed(1)}%</p>
          <p className="text-xs text-gray-500 mt-1">Average across all models</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Fallback Usage</h3>
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totals.totalFallbacks}</p>
          <p className="text-xs text-gray-500 mt-1">Times fallback model used</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Model Performance Overview</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calls
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Time
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Tokens
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fallbacks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {modelPerformance.map((perf, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{perf.model_used}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{perf.feature}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {perf.total_calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        perf.success_rate_pct >= 95
                          ? 'bg-green-100 text-green-800'
                          : perf.success_rate_pct >= 85
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {perf.success_rate_pct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1 text-sm text-gray-600">
                      <Clock className="w-3 h-3" />
                      {perf.avg_response_time_ms}ms
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                    {Math.round(perf.avg_tokens).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1 text-sm font-medium text-gray-900">
                      <DollarSign className="w-3 h-3" />
                      {Number(perf.total_cost_usd).toFixed(4)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {perf.fallback_calls > 0 ? (
                      <span className="text-yellow-600 font-medium">{perf.fallback_calls}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {modelPerformance.length === 0 && (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No performance data available</p>
              <p className="text-sm text-gray-400 mt-1">Data will appear after LLM usage</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Prompt-Specific Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prompt Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Executions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Time
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promptPerformance.map((perf, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{perf.prompt_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{perf.model_used || 'Not executed'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {perf.total_executions || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {perf.success_rate_pct ? (
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          perf.success_rate_pct >= 95
                            ? 'bg-green-100 text-green-800'
                            : perf.success_rate_pct >= 85
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {perf.success_rate_pct.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                    {perf.avg_response_time_ms ? `${perf.avg_response_time_ms}ms` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {perf.total_cost_usd ? `$${Number(perf.total_cost_usd).toFixed(4)}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Optimization Insights</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              {totals.totalFallbacks > 0 && (
                <li>
                  <strong>{totals.totalFallbacks} fallback calls</strong> detected. Consider reviewing primary model configurations for reliability.
                </li>
              )}
              {totals.avgSuccessRate < 95 && (
                <li>
                  Average success rate is <strong>{totals.avgSuccessRate.toFixed(1)}%</strong>. Target 95%+ for optimal performance.
                </li>
              )}
              {totals.totalCost > 10 && (
                <li>
                  Total spend: <strong>${totals.totalCost.toFixed(2)}</strong>. Review high-cost prompts for optimization opportunities.
                </li>
              )}
              {modelPerformance.length === 0 && (
                <li>No performance data yet. Metrics will populate as users generate reports and use AI features.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLLMPerformance;
